import { Component, HostBinding } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { RenderPackDialogComponent, RenderPackDialogData } from '../pack-dialog/render-pack-dialog.component';
import { RenderCheckoutError, renderFrameVeiled, RenderFlowStep, RenderJob, RenderNotImplementedError } from '../render-flow.model';
import { RenderFlowService } from '../render-flow.service';
import { renderDialogPanes } from '../render-home';
import { RenderLibraryFilter } from './render-library-filter.component';
import { RenderUnlockDialogComponent, RenderUnlockDialogData } from '../unlock-dialog/render-unlock-dialog.component';

// The card's download button (designer, Figma 28563:27804): black when the videos are there to take,
// grey otherwise — grey and still pressable when a payment is all that is missing ("Unlock your
// pack"), grey and off while something renders or has failed.
type LibraryDownload = 'ready' | 'locked' | 'off';

interface LibraryCard {
  modelId: string;
  name: string;
  // Where the model is: its preview to buy it, its downloads to fetch the videos again, …
  url: string;
  image?: string;
  status: string;
  download: LibraryDownload;
  // Finished while the user was elsewhere: a blue dot after the name until it is opened.
  unseen: boolean;
  // A preview or the videos under way: how far (a bar along the still's bottom edge).
  progress: number | null;
  // A failed or an unpaid preview: the "−" before the download (Figma 28548:27569, owner 2026-10-22).
  removable: boolean;
  // Still uploading: the same "−" stops the upload (owner, 2026-10-23).
  uploading: boolean;
  // A sample walked through: marked, and nothing to download — not the user's model.
  sample: boolean;
  // Paid videos that stopped (they stay for support): the still dimmed, the status on it with a red
  // dot, as on the model's page — the card read as a finished one (designer, 2026-11-02).
  stopped: boolean;
  // Paid for, its still still the preview's with the watermark: blurred until the clean one comes
  // (renderFrameVeiled).
  veiled: boolean;
}

// The lead over the cards, and what it says with none, for each view of the filter: one line on a
// desktop, every one of them, so the row keeps its height (they are stacked, the page shows one).
const LEADS: Record<RenderLibraryFilter, { some: string; none: string }> = {
  mine: { some: 'Continue to payment or restart your download.', none: 'Every model you preview will wait for you here.' },
  purchases: { some: 'Your videos, ready to download again.', none: 'Videos you buy will wait for you here.' },
  samples: { some: 'The samples you walked through, to watch again.', none: 'No samples yet. Try one on the upload page.' },
};
const LEAD_LINES: ReadonlyArray<string> = Object.values(LEADS).reduce<string[]>((lines, lead) => [...lines, lead.some, lead.none], []);

const STATUS: Record<RenderFlowStep, string> = {
  processing: 'Preview in progress',
  preview: 'Preview ready',
  checkout: 'Checkout open',
  confirming: 'Confirming payment',
  rendering: 'Rendering videos',
  downloads: 'Videos ready',
  error: 'Something went wrong',
};

// "Your library" (designer, Figma 28548:27569, 2026-09-23/24; the "−" 2026-10-22): Library in the
// header's profile menu leads here.
// Every model the user has put through the flow, newest first, on the landing's gallery cards
// without the author line (it is always the user). Under each still: the pack's download button and
// the ring that opens the model's step. After the last card, a slot starts the next model while
// others render — previews run in parallel — with empty slots to the end of its row (owner,
// 2026-09-25: her "Preview another model" button stood alone on the right). Her button stands
// beside the lead too, always (designer, 2026-10-23): shown only while the slot was below the first
// screen (2026-10-16), it went whenever a filter's view held few cards. A model at work shows
// its step and percentage, with a bar along the still. The mock lists this tab's jobs; the dev render
// simulator the user's Firestore list, every tab and device alike (docs/render-landing-port.md,
// decision 24).
@Component({
  selector: 'app-render-library',
  templateUrl: './render-library.component.html',
})
export class RenderLibraryComponent {
  @HostBinding('class') public readonly hostClass = 'render-step step-library';

  // The filter's view (Figma 28766:46464; owner, 2026-10-23): the user's own models by default.
  public filter: RenderLibraryFilter = 'mine';
  private readonly filterSubject = new BehaviorSubject<RenderLibraryFilter>('mine');
  // Samples seen at work while the page is open: "My models" shows them as they render (the header's
  // ring leads here) and keeps them until the page is left, so a card never vanishes as its run ends.
  private readonly samplesAtWork = new Set<string>();

  public readonly cards$: Observable<LibraryCard[]> = combineLatest([this.flow.jobs$, this.filterSubject]).pipe(
    map(([jobs, filter]) => {
      jobs.filter((job) => job.source.kind === 'sample' && isActive(job)).forEach((job) => this.samplesAtWork.add(job.modelId));
      return jobs
        .filter((job) => this.shows(job, filter))
        .sort((a, b) => b.createdAt - a.createdAt)
        .map((job) => this.card(job));
    }),
  );

  // Any model of the user's own, whatever the view shows: the next model is "first" only without one.
  public readonly hasOwn$: Observable<boolean> = this.flow.ownJobs$.pipe(map((jobs) => jobs.length > 0));

  // Enough empty slots to finish the next-model slot's row of three; CSS drops the ones past it.
  public readonly emptySlots = [0, 1];

  // The card whose checkout is on its way, and what went wrong with the last one.
  public busyModelId: string | null = null;
  public downloadError = '';
  // The preview whose removal (or the upload whose stop) is being confirmed, on its own card.
  public confirmingId: string | null = null;
  public confirmingUpload = false;

  constructor(
    private readonly flow: RenderFlowService,
    private readonly dialog: MatDialog,
    private readonly router: Router,
  ) {}

  public readonly leadLines = LEAD_LINES;

  public lead(cards: ReadonlyArray<LibraryCard>): string {
    return cards.length ? LEADS[this.filter].some : LEADS[this.filter].none;
  }

  // The next model's button and slot: "another" beside cards, plain "your" over a view with none of
  // them — Purchases before a purchase (designer, 2026-10-23) — and "your first" without any model.
  public nextLabel(cards: ReadonlyArray<LibraryCard>, hasOwn: boolean | null): string {
    if (!hasOwn) {
      return 'Preview your first model';
    }
    return cards.length ? 'Preview another model' : 'Preview your model';
  }

  public onFilter(filter: RenderLibraryFilter): void {
    this.filter = filter;
    this.filterSubject.next(filter);
  }

  public trackByModelId(index: number, card: LibraryCard): string {
    return card.modelId;
  }

  // Removing a preview takes two clicks: the button asks on the card, the card's Remove does. So
  // does stopping an upload — asked as one, it stops whatever the model is at by the answer: an
  // upload that has just finished has started its preview, which goes the same way.
  public onRemoveAsk(card: LibraryCard): void {
    this.confirmingId = card.modelId;
    this.confirmingUpload = card.uploading;
  }

  public onRemove(card: LibraryCard): void {
    this.confirmingId = null;
    if (this.confirmingUpload) {
      this.flow.cancelPreview(card.modelId, false);
      return;
    }
    this.flow.removeFromLibrary(card.modelId);
  }

  public downloadLabel(card: LibraryCard): string {
    switch (card.download) {
      case 'ready':
        return `Download the ${card.name} videos`;
      case 'locked':
        return `Unlock the ${card.name} videos`;
      default:
        return `${card.name}: ${card.status.toLowerCase()}, nothing to download yet`;
    }
  }

  public onDownload(card: LibraryCard): void {
    const job = this.flow.job(card.modelId);
    if (!job || this.busyModelId) {
      return;
    }
    if (card.download === 'locked') {
      this.openUnlock(job);
    } else if (card.download === 'ready') {
      this.openPack(job);
    }
  }

  // The videos there to take: the downloads page's "Optimize for platforms" card, to choose the
  // platforms and the files' name before the pack (designer, 2026-10-16).
  private openPack(job: RenderJob): void {
    this.downloadError = '';
    this.dialog.open<RenderPackDialogComponent, RenderPackDialogData>(RenderPackDialogComponent, {
      data: { modelId: job.modelId },
      autoFocus: 'dialog',
      panelClass: renderDialogPanes('render-pack-pane'),
      ariaLabelledBy: 'render-pack-title',
    });
  }

  // "Unlock your pack" (Figma 28583:58754): "Go to payment" starts this model's checkout.
  private openUnlock(job: RenderJob): void {
    this.flow.track('unlock_opened', job);
    this.dialog
      .open<RenderUnlockDialogComponent, RenderUnlockDialogData, 'pay' | undefined>(RenderUnlockDialogComponent, {
        data: { name: job.name },
        autoFocus: 'dialog',
        panelClass: renderDialogPanes('render-unlock-pane'),
        ariaLabelledBy: 'render-unlock-title',
      })
      .afterClosed()
      .subscribe((result) => {
        if (result === 'pay') {
          this.startPayment(job.modelId);
        }
      });
  }

  private async startPayment(modelId: string): Promise<void> {
    this.busyModelId = modelId;
    this.downloadError = '';
    let leaving = false;
    try {
      const next = await this.flow.beginPurchase(modelId);
      if (next.kind === 'redirect') {
        // Busy until the browser has left for Stripe: another click would open another checkout.
        leaving = true;
        window.location.href = next.url;
        return;
      }
      // The shell follows a job only once its page is open: from here, go there.
      const job = this.flow.job(modelId);
      if (job) {
        this.router.navigateByUrl(RenderFlowService.stepUrl(job));
      }
    } catch (error) {
      this.downloadError = error instanceof RenderNotImplementedError || error instanceof RenderCheckoutError ? error.message : 'Checkout could not be started. Please try again.';
    } finally {
      this.busyModelId = leaving ? modelId : null;
    }
  }

  private shows(job: RenderJob, filter: RenderLibraryFilter): boolean {
    const sample = job.source.kind === 'sample';
    switch (filter) {
      case 'samples':
        return sample;
      case 'purchases':
        return !sample && job.entitlement === 'full';
      default:
        return !sample || this.samplesAtWork.has(job.modelId);
    }
  }

  private card(job: RenderJob): LibraryCard {
    const sample = job.source.kind === 'sample';
    const unlocked = job.entitlement === 'full';
    const payable = (job.step === 'preview' || job.step === 'checkout') && !unlocked && !sample;
    const active = isActive(job);
    const stopped = job.step === 'error' && job.error?.failedStep === 'rendering';
    return {
      modelId: job.modelId,
      name: job.name,
      url: RenderFlowService.stepUrl(job),
      image: job.preview?.landscapeCard,
      status: active ? activeStatus(job) : stopped ? 'Videos stopped' : STATUS[job.step],
      download: job.step === 'downloads' && job.videos && !sample ? 'ready' : payable ? 'locked' : 'off',
      unseen: !!job.unseen,
      progress: active ? job.progress : null,
      removable: this.flow.canRemoveFromLibrary(job.modelId),
      uploading: !!job.uploading,
      sample,
      stopped,
      veiled: renderFrameVeiled(job, 'landscape'),
    };
  }
}

function isActive(job: RenderJob): boolean {
  return job.step === 'processing' || job.step === 'rendering';
}

// A model at work, as the library's upload cards put it: uploading, or the preview or the videos,
// and how far along — the header ring's number (RenderJob.progress).
function activeStatus(job: RenderJob): string {
  return `${job.uploading ? 'Uploading' : STATUS[job.step]} · ${job.progress}%`;
}
